import React, { useEffect, useState } from "react";
import { SideNavigation } from "@cloudscape-design/components";
import { Link, useNavigate } from "react-router-dom";
import { fetchAuthSession } from "aws-amplify/auth";

interface NavigationProps {
  activeHref: string;
  setActiveHref: (href: string) => void;
  adminSubMenuOpen: boolean;
  setAdminSubMenuOpen: (href: boolean) => void;
}

export function Navigation({
  activeHref,
  setActiveHref,
  adminSubMenuOpen,
  setAdminSubMenuOpen,
}: NavigationProps) {
  const navigate = useNavigate();
  const [userGroups, setUserGroups] = useState<string[]>([]);

  useEffect(() => {
    async function fetchUserGroups() {
      try {
        const { tokens } = await fetchAuthSession();
        const groups = tokens?.accessToken.payload["cognito:groups"];
        if (Array.isArray(groups)) {
          setUserGroups(groups as string[]);
        } else if (typeof groups === "string") {
          setUserGroups([groups]);
        } else {
          setUserGroups([]);
        }
      } catch (error) {
        console.error("Error fetching user groups:", error);
        setUserGroups([]);
      }
    }

    fetchUserGroups();
  }, []);

  const isSuperAdmin = userGroups.includes("super_admin");

  // Define common items for all users
  const commonItems = [
    {
      type: "link" as const,
      text: "Dashboard",
      href: "/",
    },
    {
      type: "link" as const,
      text: "Vehicle Summary",
      href: "/vehicles",
    },
    {
      type: "link" as const,
      text: "Vehicle Alarms",
      href: "/vehicles/alarms",
    },
    {
      type: "link" as const,
      text: "Monitoring & Maintenance",
      href: "/vehicle/monitoring-maintenance",
    },
    {
      type: "link-group" as const,
      text: "Data Analytics",
      href: "/vehicle/data-analytics",
      items: [
        {
          type: "link" as const,
          text: "Historical Data View",
          href: "/vehicle/data-analytics/historical-data-view"
        },
        {
          type: "link" as const,
          text: "Predictive Analytics",
          href: "/vehicle/data-Analytics/predictive-Analytics"
        },
        {
          type: "link" as const,
          text: "Cost Savings & Efficiency",
          href: "/vehicle/data-Analytics/cost-saving-efficiency"
        },
        
      ]
    },
    {
      type: "link-group" as const,
      text: "Report & Exporting",
      href: "/vehicle/report-exporting",
      items: [
        {
          type: "link" as const,
          text: "Reports",
          href: "/vehicle/report-exporting/reports"
        },
        {
          type: "link" as const,
          text: "Export Options",
          href: "/vehicle/report-exporting/export-options"
        },
        {
          type: "link" as const,
          text: "Scheduled Reports",
          href: "/vehicle/report-exporting/scheduled-report"
        },
        
      ]
    },
    {
      type: "section" as const,
      text: "Tyre Services",
      items: [
        {
          type: "link" as const,
          text: "Dashboard",
          href: "/vehicles/tyres/overview",
        },
        {
          type: "link" as const,
          text: "Tyre Summary",
          href: "/vehicles/tyres",
        },
        {
          type: "link" as const,
          text: "Tyre Alarms",
          href: "/vehicles/tyres/alarms",
        },
        {
          type: "link" as const,
          text: "Monitoring & Maintenance",
          href: "/vehicles/tyres/monitoring-maintenance",
        },
        {
          type: "link-group" as const,
          text: "Data Analytics",
          href: "/vehicles/tyres/hystorical-data-view",
          items :[
            {
              type: "link" as const,
              text: "Hystorical Data View",
              href: "/vehicles/tyres/hystorical-data-view",
            },
            {
              type: "link" as const,
              text: "Predictive Analytics",
              href: "/vehicles/tyres/predictive-analytics",
            },
            {
              type: "link" as const,
              text: "Cost Savings & Efficiency",
              href: "/vehicles/tyres/cost-savings-efficiency",
            },
          ],
        },
        {
          type: "link-group" as const,
          text: "Reporting & Exporting",
          href: "/vehicles/tyres/reports",
          items :[
            {
              type: "link" as const,
              text: "Reports",
              href: "/vehicles/tyres/reports",
            },
            {
              type: "link" as const,
              text: "Export Options",
              href: "/vehicles/tyres/export-options",
            },
            {
              type: "link" as const,
              text: "Scheduled Reports",
              href: "/vehicles/tyres/scheduled-reports",
            },
          ],
        },
      ],
    },
    {
      type: "section" as const,
      text: "Journey",
      items: [
        {
          type: "link" as const,
          text: "Dashboard",
          href: "/journey",
        },
        {
          type: "link" as const,
          text: "Journey Summary",
          href: "/journey/summary",
        },
        {
          type: "link" as const,
          text: "Journey Alarms",
          href: "/journey/alarms",
        },
        {
          type: "link" as const,
          text: "Monitoring & Maintenance",
          href: "/journey/monitoring-maintenance",
        },
        {
          type: "link-group" as const,
          text: "Data Analytics",
          href: "/journey/hystorical-data-view",
          items :[
            {
              type: "link" as const,
              text: "Hystorical Data View",
              href: "/journey/hystorical-data-view",
            },
            {
              type: "link" as const,
              text: "Predictive Analytics",
              href: "/journey/predictive-analytics",
            },
            {
              type: "link" as const,
              text: "Cost Savings & Efficiency",
              href: "/journey/cost-saving-efficiency",
            },
          ],
        },
        {
          type: "link-group" as const,
          text: "Reporting & Exporting",
          href: "/journey/reports",
          items :[
            {
              type: "link" as const,
              text: "Reports",
              href: "/journey/reports",
            },
            {
              type: "link" as const,
              text: "Export Options",
              href: "/journey/export-options",
            },
            {
              type: "link" as const,
              text: "Scheduled Reports",
              href: "/journey/scheduled-reports",
            },
          ],
        },
    
    

        
      ],
    },
       
  ];

  // Define admin and system items
  const adminItems = [
    {
      type: "section" as const,
      text: "Admin",
      items: [
        {
          type: "link" as const,
          text: "Dashboard",
          href: "/admin",
        },
        {
          type: "link" as const,
          text: "User Detail",
          href: "/admin/users",
        },
      ],
    },
  ];

  const systemItems = [
    {
      type: "section" as const,
      text: "System",
      items: [
        {
          type: "link" as const,
          text: "Dashboard",
          href: "/system",
        },
        {
          type: "link" as const,
          text: "Tenant Detail",
          href: "/system/tenants",
        },
      ],
    },
  ];

  const backendItems = [
    {
      type: "section" as const,
      text: "Backend",
      items: [
        {
          type: "link" as const,
          text: "Backend",
          href: "/backend",
        },
      ],
    },
  ];

  return (
    <SideNavigation
      activeHref={activeHref}
      header={{ href: "/", text: "Vehicles" }}
      onFollow={(event) => {
        if (!event.detail.external) {
          event.preventDefault();
          navigate(event.detail.href);
          setActiveHref(event.detail.href);
        }
      }}
      items={[
        ...commonItems,
        ...(isSuperAdmin
          ? [...adminItems, ...systemItems]
          : [
              ...(userGroups.includes("tenant_admin") ? adminItems : []),
              ...(userGroups.includes("super_admin") ? systemItems : []),
            ]),
        ...backendItems,
      ]}
    />
  );
}
